package fr.manyverse.app.presentation.components.modules.digicode

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Dialpad
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.DigicodeStatus
import fr.manyverse.app.presentation.theme.success
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.digicode_active_digicodes
import manyverse.shared.generated.resources.digicode_attempt_correct
import manyverse.shared.generated.resources.digicode_attempt_wrong
import manyverse.shared.generated.resources.digicode_attempts_history
import manyverse.shared.generated.resources.digicode_cancel_activity
import manyverse.shared.generated.resources.digicode_close
import manyverse.shared.generated.resources.digicode_digit_count_label
import manyverse.shared.generated.resources.digicode_target_player
import manyverse.shared.generated.resources.module_status_active
import manyverse.shared.generated.resources.module_status_cancelled
import manyverse.shared.generated.resources.module_status_failed
import manyverse.shared.generated.resources.module_status_success
import manyverse.shared.generated.resources.module_status_timeout
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * Section displayed in ModulesTab for the GM to monitor an active digicode challenge.
 */
@Composable
fun DigicodeGmSection(
    activeDigicode: ActiveDigicode?,
    targetPlayerName: String,
    onCancel: (digicodeId: String) -> Unit,
    onClose: (digicodeId: String) -> Unit,
    modifier: Modifier = Modifier
) {
    if (activeDigicode == null) return

    val targetName = targetPlayerName

    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(top = 16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = stringResource(Res.string.digicode_active_digicodes),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.primary
        )

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceVariant
            )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                // Header row: target player + status indicator + action button
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Dialpad,
                        contentDescription = stringResource(activeDigicode.status.labelRes()),
                        tint = when (activeDigicode.status) {
                            DigicodeStatus.ACTIVE -> MaterialTheme.colorScheme.primary
                            DigicodeStatus.SUCCESS -> MaterialTheme.colorScheme.success
                            DigicodeStatus.FAILED, DigicodeStatus.TIMEOUT -> MaterialTheme.colorScheme.error
                            DigicodeStatus.CANCELLED -> MaterialTheme.colorScheme.onSurfaceVariant
                        },
                        modifier = Modifier.size(24.dp)
                    )

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "${stringResource(Res.string.digicode_target_player)}: $targetName",
                            style = MaterialTheme.typography.titleSmall
                        )
                        Text(
                            text = stringResource(
                                Res.string.digicode_digit_count_label,
                                activeDigicode.digitCount
                            ) + " · ${activeDigicode.maxAttempts} essais",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    if (activeDigicode.status != DigicodeStatus.ACTIVE) {
                        IconButton(onClick = { onClose(activeDigicode.id) }) {
                            Icon(
                                imageVector = Icons.Default.Close,
                                contentDescription = stringResource(Res.string.digicode_close),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }

                // Attempts history
                if (activeDigicode.attempts.isNotEmpty()) {
                    Text(
                        text = stringResource(Res.string.digicode_attempts_history),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    activeDigicode.attempts.forEach { attempt ->
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                imageVector = if (attempt.isSuccess) Icons.Default.Check else Icons.Default.Close,
                                contentDescription = stringResource(if (attempt.isSuccess) Res.string.digicode_attempt_correct else Res.string.digicode_attempt_wrong),
                                tint = if (attempt.isSuccess) MaterialTheme.colorScheme.success else MaterialTheme.colorScheme.error,
                                modifier = Modifier.size(16.dp)
                            )
                            Text(
                                text = attempt.value,
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    }
                }

                // Cancel button (text version) for ACTIVE status
                if (activeDigicode.status == DigicodeStatus.ACTIVE) {
                    TextButton(
                        onClick = { onCancel(activeDigicode.id) },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = stringResource(Res.string.digicode_cancel_activity),
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
        }
    }
}

private fun DigicodeStatus.labelRes(): StringResource = when (this) {
    DigicodeStatus.ACTIVE -> Res.string.module_status_active
    DigicodeStatus.SUCCESS -> Res.string.module_status_success
    DigicodeStatus.FAILED -> Res.string.module_status_failed
    DigicodeStatus.TIMEOUT -> Res.string.module_status_timeout
    DigicodeStatus.CANCELLED -> Res.string.module_status_cancelled
}
